import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface PricingData {
  listing_id: number;
  title: string;
  current_tier: string;
  subscription_status: string;
  subscription_expires: string | null;
  renewal_price_cents: number;
  renewal_price_display: string;
  standard_price_display: string;
  discount: string;
  all_tiers: Array<{
    tier: string;
    name: string;
    renewal: string;
    standard: string;
  }>;
}

export default function RenewPage() {
  const { id } = useParams();
  const [data, setData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/.netlify/functions/checkout?id=${id}`)
      .then(res => res.json())
      .then(d => {
        if (d.error) {
          setError(d.error);
        } else {
          setData(d);
          setSelectedTier(d.current_tier);
        }
      })
      .catch(() => setError('Failed to load listing'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCheckout = async () => {
    setRedirecting(true);
    try {
      const res = await fetch('/.netlify/functions/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: id,
          tier: selectedTier,
          billing_period: billingPeriod,
        }),
      });
      const result = await res.json();
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        setError(result.error || 'Failed to create checkout session');
        setRedirecting(false);
      }
    } catch {
      setError('Network error');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="renew-page">
        <div className="renew-container">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="renew-page">
        <div className="renew-container">
          <h1>Renewal</h1>
          <div className="error-box">{error}</div>
          <a href="/" className="btn-back">← Back to MarineLink</a>
        </div>
      </div>
    );
  }

  const tierBenefits: Record<string, string[]> = {
    basic: ['Directory listing', 'Contact details', '1 image', 'Direct enquiries', 'SEO backlink'],
    featured: ['Everything in Basic', 'Featured badge', '10 images', 'Services list', 'Customer reviews', 'Priority search', '3× more clicks'],
    premium: ['Everything in Standard', 'Premium badge', 'Hero image', 'Top placement', 'Editable listing', 'Analytics dashboard', 'Lead capture form'],
  };

  const tierPrices: Record<string, { monthly: number; annual: number }> = {
    basic: { monthly: 9, annual: 108 },
    featured: { monthly: 9, annual: 108 },
    premium: { monthly: 19, annual: 228 },
  };

  const selectedPrice = tierPrices[selectedTier] || tierPrices.basic;

  return (
    <div className="renew-page">
      <div className="renew-container">
        <div className="renew-header">
          <h1>Renew Your MarineLink Listing</h1>
          <p className="renew-subtitle">{data?.title}</p>
          <span className="renew-badge">{data?.subscription_status === 'active' ? '✅ Active' : '⚠️ Expired'}</span>
        </div>

        <div className="billing-toggle">
          <button
            className={billingPeriod === 'monthly' ? 'active' : ''}
            onClick={() => setBillingPeriod('monthly')}
          >Monthly</button>
          <button
            className={billingPeriod === 'annual' ? 'active' : ''}
            onClick={() => setBillingPeriod('annual')}
          >Annual <span className="save-badge">Best value</span></button>
        </div>

        <div className="tier-grid">
          {data?.all_tiers.map((t) => (
            <div
              key={t.tier}
              className={`tier-card ${selectedTier === t.tier ? 'selected' : ''} ${t.tier}`}
              onClick={() => setSelectedTier(t.tier)}
            >
              {t.tier === 'featured' && <span className="popular-tag">Most Popular</span>}
              {t.tier === 'premium' && <span className="best-tag">Best Value</span>}
              <h3>{t.name}</h3>
              <div className="tier-price">
                <span className="price">{t.renewal}</span>
                <span className="period">/mo</span>
              </div>
              {billingPeriod === 'annual' && (
                <div className="annual-note">Billed annually (${tierPrices[t.tier]?.annual || 108}/yr)</div>
              )}
              <ul className="tier-benefits">
                {tierBenefits[t.tier]?.map((b, i) => (
                  <li key={i}>✓ {b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="renew-summary">
          <div className="summary-row">
            <span>Selected tier:</span>
            <strong>{data?.all_tiers.find(t => t.tier === selectedTier)?.name}</strong>
          </div>
          <div className="summary-row">
            <span>Billing:</span>
            <strong>{billingPeriod === 'annual' ? `Annual ($${selectedPrice.annual}/yr)` : 'Monthly'}</strong>
          </div>
          <div className="summary-row total">
            <span>Total:</span>
            <strong>
              {billingPeriod === 'annual'
                ? `$${selectedPrice.annual}/year`
                : `$${selectedPrice.monthly}/month`}
            </strong>
          </div>
        </div>

        <button
          className="checkout-btn"
          onClick={handleCheckout}
          disabled={redirecting}
        >
          {redirecting ? 'Redirecting to checkout...' : 'Continue to Payment →'}
        </button>

        <div className="renew-trust">
          <p>🔒 Secure payment via Stripe</p>
          <p>Cancel anytime. No hidden fees.</p>
        </div>
      </div>
    </div>
  );
}
